import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useEffect, useState } from 'react';
import { VERSES } from '@/lib/verses';

/**
 * SplashScreen — Phase 153.
 *
 * Full-screen, branded "SplashPad is loading" state used as the Suspense
 * fallback for lazy route chunks (and anywhere a long first load happens).
 * Visually identical to the inline first-byte splash in public/index.html so
 * the hand-off from HTML → React is seamless: same navy, same rippling mark,
 * same progress bar, same rotating Bible verse.
 *
 * `inline` renders a compact (non-fixed) variant for in-page waits.
 */
export const SplashScreen = ({ inline = false, subtitle = 'SplashPad is loading', testId = 'splash-screen' }) => {
    const { t: t42 } = useLocaleR42();
    const [idx, setIdx] = useState(() => Math.floor(Math.random() * VERSES.length));
    const [fade, setFade] = useState(false);

    useEffect(() => {
        const t = setInterval(() => {
            setFade(true);
            setTimeout(() => { setIdx((i) => (i + 1) % VERSES.length); setFade(false); }, 400);
        }, 4200);
        return () => clearInterval(t);
    }, []);

    const verse = VERSES[idx % VERSES.length];

    const body = (
        <div className="flex flex-col items-center px-6 text-center text-white" role="status" aria-live="polite" aria-label={subtitle} data-testid={testId}>
            <div className="relative mb-5 h-[104px] w-[104px]" aria-hidden="true">
                <span className="absolute inset-0 rounded-full border-2 border-[#00d4ff]/60 motion-reduce:animate-none" style={{ animation: 'sp-ripple 2.4s ease-out infinite' }} />
                <span className="absolute inset-0 rounded-full border-2 border-[#00d4ff]/60 motion-reduce:animate-none" style={{ animation: 'sp-ripple 2.4s ease-out infinite', animationDelay: '.8s' }} />
                <span className="absolute inset-0 rounded-full border-2 border-[#00d4ff]/60 motion-reduce:animate-none" style={{ animation: 'sp-ripple 2.4s ease-out infinite', animationDelay: '1.6s' }} />
                <img src="/pwa-icon-192.png" alt="" width={104} height={104} className="relative z-[2] h-[104px] w-[104px] rounded-[28px] bg-white p-2.5 shadow-[0_18px_45px_rgba(0,0,0,0.45)] motion-reduce:animate-none" style={{ animation: 'sp-bob 2.4s ease-in-out infinite' }} />
            </div>
            <div className="font-display text-[26px] font-bold tracking-tight">{t42("Splash")}<span className="text-[#00d4ff]">{t42("Pad")}</span></div>
            <div className="mt-1.5 text-[13px] text-white/65" data-testid={`${testId}-subtitle`}>
                {subtitle}
                <span className="ml-0.5 inline-flex gap-[3px] align-middle" aria-hidden="true">
                    {[0, 1, 2].map((n) => (
                        <span key={n} className="inline-block h-[5px] w-[5px] rounded-full bg-[#00d4ff] motion-reduce:animate-none" style={{ animation: 'sp-dot 1.2s ease-in-out infinite', animationDelay: `${n * 0.2}s` }} />
                    ))}
                </span>
            </div>
            <div className="relative mt-5 h-1 w-[min(260px,70vw)] overflow-hidden rounded-full bg-white/10" aria-hidden="true">
                <i className="absolute inset-y-0 left-0 w-[40%] rounded-full bg-gradient-to-r from-[#00d4ff] to-[#7be9ff] motion-reduce:animate-none" style={{ animation: 'sp-slide 1.6s ease-in-out infinite' }} />
            </div>
            <div className={`mx-auto mt-7 min-h-[72px] max-w-[420px] transition-opacity duration-300 ${fade ? 'opacity-0' : 'opacity-100'}`} data-testid={`${testId}-verse`}>
                <p className="m-0 text-[15px] italic leading-relaxed text-white/90">“{verse.text}”</p>
                <small className="mt-1.5 block text-[12px] font-semibold uppercase tracking-[0.06em] text-[#ffd700]">{verse.source}</small>
            </div>
            <style>{`
                @keyframes sp-ripple{0%{transform:scale(.9);opacity:.9}100%{transform:scale(1.9);opacity:0}}
                @keyframes sp-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
                @keyframes sp-slide{0%{left:-40%}100%{left:100%}}
                @keyframes sp-dot{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-4px);opacity:1}}
            `}</style>
        </div>
    );

    if (inline) {
        return <div className="flex min-h-[60vh] items-center justify-center rounded-3xl bg-[#0a1628] py-10">{body}</div>;
    }
    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0a1628] pt-[env(safe-area-inset-top)]" data-testid={`${testId}-fullscreen`}>
            {body}
        </div>
    );
};

export default SplashScreen;
